// EventsPane — DashboardWall view-mode right pane.
//
// Lives in the wall surface, slides in from the right edge as a tab the
// operator can hover/click/pin. Mirrors mockups/cameras-dashboard-v7.html
// `.right-pane[data-personality="events"]`. Renders a merged timeline
// across every camera on the active dashboard:
//   - Fetch events for each tile's doorbot_id in parallel
//     (eventsClient.listForCamera, ~10 each).
//   - Merge + sort descending by timestamp; bucket by day-label.
//   - Search input filters by description + meta substring.
//   - Pulse on the rotated tab when polling discovers new events
//     (refresh fires once per minute via setInterval).
//
// Edit-mode swaps the pane personality to "library" — owned by wave 4 —
// and we cleanly tear our state down on personality change. The pane's
// open/pinned state is owned by the caller (DashboardWall) so it can
// share localStorage with whatever the library pane chooses to persist.

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type JSX,
} from "react";
import { ApiError } from "@/api";
import {
  eventsClient,
  type CameraEvent,
} from "@/api/eventsClient";
import type { Camera } from "@/api/camerasClient";
import type { DashboardTile } from "@/types/dashboard";

const POLL_INTERVAL_MS = 60_000;
/** Per-camera page cap — we don't want the wall pane to drag down on a
 *  busy dashboard. 8 keeps the merged timeline lively without thrashing. */
const PER_CAM_LIMIT = 8;

export interface EventsPaneCameraLookup {
  /** Friendly name + location, keyed by doorbot_id. Best-effort; missing
   *  ids fall back to a synthesized "Camera <short id>". */
  cameras: Map<string, Camera>;
  locationNames: Map<string, string>;
}

export interface EventsPaneProps {
  /** Tiles on the active dashboard — drives which cameras we fetch. */
  tiles: DashboardTile[];
  /** Camera/location catalog for descriptive metadata in event rows. */
  catalog: EventsPaneCameraLookup;
  /** Open / closed state. Caller owns persistence (localStorage etc.). */
  isOpen: boolean;
  /** Pinned state — when true the pane stays open without hover. */
  isPinned: boolean;
  /** Click on the rotated tab toggles open/closed (caller decides). */
  onToggleOpen: () => void;
  /** Click the pin icon → toggle pinned. */
  onTogglePinned: () => void;
}

interface EnrichedEvent {
  ev: CameraEvent;
  /** Wall-clock for sorting. NaN-safe — events with no time bucket last. */
  timeMs: number;
  /** Human label e.g. "Person detected". */
  desc: string;
  /** Sub-line "Front Door — Exterior · ZAS6". */
  meta: string;
  /** Mockup ev-icon class. */
  kindClass: "kind-motion" | "kind-person" | "kind-vehicle" | "kind-alarm";
}

function fallbackCameraName(doorbotId: string): string {
  const tail = doorbotId.length > 6 ? doorbotId.slice(-6) : doorbotId;
  return `Camera ${tail}`;
}

function eventTimeMs(ev: CameraEvent): number {
  if (ev.start_ms && ev.start_ms > 0) return ev.start_ms;
  if (ev.created_at) {
    const d = new Date(ev.created_at);
    if (!Number.isNaN(d.getTime())) return d.getTime();
  }
  return 0;
}

function descLabelFor(ev: CameraEvent): string {
  if (ev.person_detected) return "Person detected";
  if (!ev.kind) return "Motion detected";
  const cleaned = ev.kind.replaceAll("_", " ");
  return cleaned.charAt(0).toUpperCase() + cleaned.slice(1);
}

function kindClassFor(ev: CameraEvent): EnrichedEvent["kindClass"] {
  const k = (ev.kind ?? "").toLowerCase();
  if (k.includes("person") || ev.person_detected) return "kind-person";
  if (k.includes("vehicle") || k.includes("car")) return "kind-vehicle";
  if (k.includes("alarm") || k.includes("ding")) return "kind-alarm";
  return "kind-motion";
}

function dayLabelFor(timeMs: number, now: number): string {
  if (!timeMs) return "Unknown";
  const eventDate = new Date(timeMs);
  const startOfDay = new Date(now);
  startOfDay.setHours(0, 0, 0, 0);
  const yesterday = new Date(startOfDay);
  yesterday.setDate(startOfDay.getDate() - 1);
  if (timeMs >= startOfDay.getTime()) {
    if (now - timeMs <= 60 * 60_000) return "Just now";
    return "Earlier today";
  }
  if (timeMs >= yesterday.getTime()) return "Yesterday";
  return eventDate.toLocaleDateString();
}

function whenLabelFor(timeMs: number, now: number): string {
  if (!timeMs) return "—";
  const delta = now - timeMs;
  if (delta < 60_000) {
    const s = Math.max(0, Math.floor(delta / 1_000));
    return `${s}s\nago`;
  }
  if (delta < 60 * 60_000) {
    return `${Math.floor(delta / 60_000)}m\nago`;
  }
  const startOfDay = new Date(now);
  startOfDay.setHours(0, 0, 0, 0);
  if (timeMs >= startOfDay.getTime()) {
    return new Date(timeMs).toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
  }
  return new Date(timeMs).toLocaleString([], {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
}

function evSvgFor(kindClass: EnrichedEvent["kindClass"]): JSX.Element {
  if (kindClass === "kind-person") {
    return (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8zm0 2c-2.7 0-8 1.3-8 4v2h16v-2c0-2.7-5.3-4-8-4z" />
      </svg>
    );
  }
  if (kindClass === "kind-vehicle") {
    return (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M19 7H5L3 13v6h2v-2h14v2h2v-6l-2-6zM6.5 16a1.5 1.5 0 1 1 0-3 1.5 1.5 0 0 1 0 3zm11 0a1.5 1.5 0 1 1 0-3 1.5 1.5 0 0 1 0 3z" />
      </svg>
    );
  }
  if (kindClass === "kind-alarm") {
    return (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path d="M12 22a2 2 0 0 0 2-2h-4a2 2 0 0 0 2 2zm6-6V11c0-3.1-1.6-5.6-4.5-6.3V4a1.5 1.5 0 1 0-3 0v.7C7.6 5.4 6 7.9 6 11v5l-2 2v1h16v-1l-2-2z" />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M13 2 4 14h7l-1 8 9-12h-7l1-8z" />
    </svg>
  );
}

/**
 * Fan out per-camera fetches for the tiles, merge + sort, and dedupe by
 * ding_id (cameras occasionally re-emit the same event under a different
 * doorbot_id alias). Returns up to ~120 events at the high end (16 tiles
 * × 8 each), more than enough for a casual scroll-through.
 */
async function fetchEventsForTiles(
  tiles: DashboardTile[],
  catalog: EventsPaneCameraLookup,
  signal: AbortSignal,
): Promise<EnrichedEvent[]> {
  const uniqueIds = Array.from(new Set(tiles.map((t) => t.doorbot_id)));
  if (uniqueIds.length === 0) return [];
  // BFF requires a bounded time window (rejects null start/end with
  // invalid_time_format). Default to "last 7 days → now" — enough to surface
  // a non-empty timeline for most operators while staying under the 30-day cap.
  const end = new Date().toISOString();
  const start = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString();
  const results = await Promise.all(
    uniqueIds.map((id) =>
      eventsClient
        .listForCamera(id, { start, end, limit: PER_CAM_LIMIT, signal })
        .then((resp) => resp.items)
        // Per-camera failures are non-fatal for the merged pane — the
        // operator still sees the cameras that did return.
        .catch((err: unknown) => {
          if (err instanceof ApiError && (err.status === 403 || err.status === 404)) {
            return [] as CameraEvent[];
          }
          if (signal.aborted) return [] as CameraEvent[];
          // Other errors: swallow per-camera so one outage doesn't blank
          // the whole pane. We still return [] for the row so the merged
          // list renders the surviving cameras' events.
          return [] as CameraEvent[];
        }),
    ),
  );
  const seen = new Set<string>();
  const merged: EnrichedEvent[] = [];
  for (const items of results) {
    for (const ev of items) {
      if (seen.has(ev.ding_id)) continue;
      seen.add(ev.ding_id);
      const cam = catalog.cameras.get(ev.doorbot_id);
      const camName = cam?.description?.trim() || fallbackCameraName(ev.doorbot_id);
      const locName = cam?.location_id ? catalog.locationNames.get(cam.location_id) ?? null : null;
      const meta = locName ? `${camName} · ${locName}` : camName;
      merged.push({
        ev,
        timeMs: eventTimeMs(ev),
        desc: descLabelFor(ev),
        meta,
        kindClass: kindClassFor(ev),
      });
    }
  }
  merged.sort((a, b) => b.timeMs - a.timeMs);
  return merged;
}

export function EventsPane({
  tiles,
  catalog,
  isOpen,
  isPinned,
  onToggleOpen,
  onTogglePinned,
}: EventsPaneProps): JSX.Element {
  const [events, setEvents] = useState<EnrichedEvent[] | null>(null);
  const [search, setSearch] = useState<string>("");
  // newCount is the number of events the most recent poll surfaced that
  // weren't in the previous result set; we light the pulse + count chip
  // until the operator opens the pane (which clears the marker).
  const [newCount, setNewCount] = useState<number>(0);
  const seenIdsRef = useRef<Set<string>>(new Set());
  // Snapshot tiles + catalog refs to keep the polling effect deps stable
  // (we don't want a tile re-render to thrash the interval).
  const tilesRef = useRef(tiles);
  const catalogRef = useRef(catalog);
  tilesRef.current = tiles;
  catalogRef.current = catalog;

  // Initial fetch + polling — only while the pane is OPEN. A closed pane
  // is a "tab" affordance with no visible event list, so per-camera
  // events fetches would be redundant API traffic. Re-running the effect
  // when isOpen flips to true triggers a fresh fetch and starts the
  // interval; closing tears down the interval and aborts any in-flight
  // request.
  useEffect(() => {
    if (!isOpen) return;
    let alive = true;
    const ctrl = new AbortController();
    const run = async (markSeen: boolean): Promise<void> => {
      try {
        const merged = await fetchEventsForTiles(
          tilesRef.current,
          catalogRef.current,
          ctrl.signal,
        );
        if (!alive || ctrl.signal.aborted) return;
        if (markSeen) {
          // Initial run primes the seen-set with no "new" pulse.
          seenIdsRef.current = new Set(merged.map((m) => m.ev.ding_id));
          setNewCount(0);
        } else {
          let fresh = 0;
          for (const m of merged) {
            if (!seenIdsRef.current.has(m.ev.ding_id)) {
              seenIdsRef.current.add(m.ev.ding_id);
              fresh++;
            }
          }
          if (fresh > 0) setNewCount((prev) => prev + fresh);
        }
        setEvents(merged);
      } catch {
        // Top-level errors only happen if every per-camera fetch threw
        // something other than 403/404; render an empty list rather than
        // a banner — the operator will see the pane is healthy on the
        // next poll.
        if (alive && !ctrl.signal.aborted) setEvents([]);
      }
    };
    void run(true);
    const id = window.setInterval(() => {
      void run(false);
    }, POLL_INTERVAL_MS);
    return () => {
      alive = false;
      ctrl.abort();
      window.clearInterval(id);
    };
    // Re-key the effect on the SET of doorbot_ids — switching dashboards
    // resets the seen-set + clears the new-count without retriggering
    // for tile reorders within the same dashboard.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, tiles.map((t) => t.doorbot_id).sort().join("|")]);

  // Clear the "new" badge whenever the operator opens the pane.
  useEffect(() => {
    if (isOpen) setNewCount(0);
  }, [isOpen]);

  const handleTabClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>): void => {
      // Don't toggle when the pin button (nested) was the click target.
      if ((e.target as HTMLElement).closest(".v7-rp-pin-btn")) return;
      onToggleOpen();
    },
    [onToggleOpen],
  );

  const filtered = useMemo<EnrichedEvent[]>(() => {
    if (!events) return [];
    const q = search.trim().toLowerCase();
    if (!q) return events;
    return events.filter(
      (e) =>
        e.desc.toLowerCase().includes(q) ||
        e.meta.toLowerCase().includes(q),
    );
  }, [events, search]);

  const grouped = useMemo<{ label: string; events: EnrichedEvent[] }[]>(() => {
    if (filtered.length === 0) return [];
    const now = Date.now();
    const order: string[] = [];
    const buckets = new Map<string, EnrichedEvent[]>();
    for (const e of filtered) {
      const label = dayLabelFor(e.timeMs, now);
      if (!buckets.has(label)) {
        buckets.set(label, []);
        order.push(label);
      }
      buckets.get(label)!.push(e);
    }
    return order.map((label) => ({ label, events: buckets.get(label) ?? [] }));
  }, [filtered]);

  const rootClass = [
    "v7-right-pane",
    isOpen || isPinned ? "is-open" : "",
    isPinned ? "is-pinned" : "",
  ]
    .filter(Boolean)
    .join(" ");

  const totalLabel =
    events === null ? "Loading…" : `${events.length} recent`;

  return (
    <aside
      className={rootClass}
      data-personality="events"
      aria-label="Events timeline"
    >
      <div
        className="v7-right-pane__tab"
        role="button"
        tabIndex={0}
        onClick={handleTabClick}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onToggleOpen();
          }
        }}
        aria-label={isOpen ? "Collapse events pane" : "Expand events pane"}
      >
        <span className="ico" aria-hidden="true">
          <svg viewBox="0 0 24 24">
            <path d="M12 6V2L7 7l5 5V8a4 4 0 1 1-4 4H6a6 6 0 1 0 6-6z" />
          </svg>
        </span>
        <span className="tab-rotate">Events</span>
        {newCount > 0 && (
          <span
            className="pulse"
            title={`${newCount} new ${newCount === 1 ? "event" : "events"}`}
            aria-label={`${newCount} new`}
          />
        )}
      </div>
      <div className="v7-right-pane__body">
        <div className="v7-right-pane__head">
          <div className="v7-right-pane__title-row">
            <h2 className="v7-right-pane__title">Events timeline</h2>
            <span className="v7-right-pane__count">
              {newCount > 0
                ? `${newCount} new`
                : totalLabel}
            </span>
            <button
              type="button"
              className="v7-rp-pin-btn"
              title={isPinned ? "Unpin events pane" : "Keep events pane open"}
              aria-label={isPinned ? "Unpin events pane" : "Keep events pane open"}
              aria-pressed={isPinned}
              onClick={onTogglePinned}
            >
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M14 4l6 6-3 3-1-1-4 4v4l-2 2-4-4-1 1-1-1 1-1-4-4 2-2h4l4-4-1-1 3-3z" />
              </svg>
            </button>
          </div>
          <div className="v7-pane-search">
            <span className="ico" aria-hidden="true">
              <svg viewBox="0 0 24 24">
                <path d="M15.5 14h-.8l-.3-.3a6.5 6.5 0 1 0-.7.7l.3.3v.8l5 5 1.5-1.5-5-5zm-6 0a4.5 4.5 0 1 1 0-9 4.5 4.5 0 0 1 0 9z" />
              </svg>
            </span>
            <input
              type="text"
              placeholder="Search events…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              aria-label="Search events"
            />
          </div>
        </div>
        <div className="v7-right-pane__list">
          {events === null && (
            <div className="v7-events-empty">Loading events…</div>
          )}
          {events !== null && filtered.length === 0 && (
            <div className="v7-events-empty">
              {events.length === 0
                ? tiles.length === 0
                  ? "Add cameras to see events here."
                  : "No events on these cameras yet."
                : `No events match "${search}".`}
            </div>
          )}
          {grouped.map((group) => (
            <div key={group.label}>
              <div className="v7-event-day-header">{group.label}</div>
              {group.events.map((entry) => {
                const when = whenLabelFor(entry.timeMs, Date.now());
                return (
                  <div key={entry.ev.ding_id} className="v7-event-row">
                    <span className="when">
                      {when.split("\n").map((line, i) => (
                        <span key={i}>
                          {line}
                          {i === 0 && when.includes("\n") && <br />}
                        </span>
                      ))}
                    </span>
                    <div className="body">
                      <span className="desc">
                        <span className={`ev-icon ${entry.kindClass}`}>
                          {evSvgFor(entry.kindClass)}
                        </span>
                        {entry.desc}
                      </span>
                      <span className="meta">{entry.meta}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </aside>
  );
}
